/**
 * Tokenization and OR matching for local in-page search (`match="any"`).
 * Exact mode stays in SearchContext and uses the full query as one substring.
 */

export type LocalSearchMatchMode = "any" | "exact";

export type LocalSearchAtomKind = "phrase" | "token";

export type LocalSearchAtom = {
    kind: LocalSearchAtomKind;
    value: string;
};

export type LocalSearchMatch = {
    text: string;
    start: number;
    end: number;
};

/** Unicode letters/numbers plus ASCII and typographic apostrophes. */
const TOKEN_RUN = /(?:\p{L}|\p{N}|['’])+/yu;

/**
 * Maps an authored `match` attribute to a mode.
 * Only the exact string `"any"` is OR mode (same case-sensitive compare as `search === "local"`).
 * Omitted, `"exact"`, empty, and any other value become `"exact"`.
 * Invalid non-empty values also `console.warn` with the raw string.
 */
export function normalizeMatchMode(raw: string | undefined): LocalSearchMatchMode {
    if (raw === "any") {
        return "any";
    }
    if (raw === "exact" || raw === undefined || raw === "") {
        return "exact";
    }
    console.warn(`Invalid match attribute "${raw}"; treating as "exact"`);
    return "exact";
}

/**
 * Walks `query` left to right into phrase and token atoms.
 * Duplicate values are dropped within the same kind (first occurrence wins).
 * Duplicate folding is case-insensitive unless `caseSensitive` is true.
 */
export function tokenizeAnyQuery(query: string, caseSensitive: boolean): LocalSearchAtom[] {
    const atoms: LocalSearchAtom[] = [];
    const seenByKind: Record<LocalSearchAtomKind, Set<string>> = {
        phrase: new Set<string>(),
        token: new Set<string>(),
    };

    let i = 0;
    while (i < query.length) {
        if (query[i] === '"') {
            i += 1;
            let value = "";
            while (i < query.length && query[i] !== '"') {
                value += query[i];
                i += 1;
            }
            if (i < query.length) {
                i += 1;
            }
            pushAtom(atoms, seenByKind, "phrase", value, caseSensitive);
            continue;
        }

        TOKEN_RUN.lastIndex = i;
        const tokenMatch = TOKEN_RUN.exec(query);
        if (tokenMatch) {
            pushAtom(atoms, seenByKind, "token", tokenMatch[0], caseSensitive);
            i += tokenMatch[0].length;
            continue;
        }

        i += 1;
    }

    return atoms;
}

/**
 * Finds OR matches for `atoms` in one content block.
 * Phrase atoms are accepted first (query order, then start offset), then token atoms.
 * A later range whose `[start, end)` overlaps an already accepted hit is dropped.
 * Accepted hits are returned in document order (`start` ascending).
 */
export function findAnyModeMatches(
    blockText: string,
    atoms: LocalSearchAtom[],
    caseSensitive: boolean,
    wholeWord: boolean,
): LocalSearchMatch[] {
    const accepted: LocalSearchMatch[] = [];

    const consider = (atom: LocalSearchAtom): void => {
        if (!atom.value) {
            return;
        }
        for (const match of findAtomMatches(blockText, atom.value, caseSensitive, wholeWord)) {
            if (!accepted.some((existing) => rangesOverlap(existing, match))) {
                accepted.push(match);
            }
        }
    };

    for (const atom of atoms) {
        if (atom.kind === "phrase") {
            consider(atom);
        }
    }
    for (const atom of atoms) {
        if (atom.kind === "token") {
            consider(atom);
        }
    }

    accepted.sort((a, b) => a.start - b.start);
    return accepted;
}

function pushAtom(
    atoms: LocalSearchAtom[],
    seenByKind: Record<LocalSearchAtomKind, Set<string>>,
    kind: LocalSearchAtomKind,
    value: string,
    caseSensitive: boolean,
): void {
    if (!value) {
        return;
    }
    const key = caseSensitive ? value : value.toLowerCase();
    if (seenByKind[kind].has(key)) {
        return;
    }
    seenByKind[kind].add(key);
    atoms.push({ kind, value });
}

function findAtomMatches(
    text: string,
    atom: string,
    caseSensitive: boolean,
    wholeWord: boolean,
): LocalSearchMatch[] {
    const escaped = escapeRegex(atom);
    const pattern = wholeWord ? `\\b${escaped}\\b` : escaped;
    const regex = new RegExp(pattern, caseSensitive ? "g" : "gi");
    const matches: LocalSearchMatch[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        matches.push({
            text: match[0],
            start: match.index,
            end: match.index + match[0].length,
        });
    }

    return matches;
}

function rangesOverlap(a: LocalSearchMatch, b: LocalSearchMatch): boolean {
    return a.start < b.end && b.start < a.end;
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
